import { coupleMatchTokens, type CoupleNameParts } from './couple';
import { containsWord, foldForMatching } from './text-match';

/**
 * What a message says about a wedding, measured against what we know about
 * that wedding (PRD §F4).
 *
 * The matching cascade proves identity twice — a Gmail thread id, a header
 * chain — and then, at Tier 3, stops proving anything. Tier 3 used to reason
 * "this is from a vendor with exactly one open inquiry, so it is about that
 * inquiry" without reading the message at all. On a live mailbox that filed a
 * DJ's conversation about one couple under a different one, and it filed his
 * Zoom invite for a third couple there too. Both were the only Tier 3 mistakes
 * ever made, and both were mail that said nothing whatsoever about the wedding
 * it was filed under.
 *
 * So the question this answers is the direct one: does the message mention
 * anything we actually know about this wedding? The couple's names, the date,
 * the venue. A message that mentions one of them is about it. A message that
 * mentions none of them is not evidence of anything, and a tier that files on
 * no evidence is a tier that eventually files wrong.
 *
 * The inverse framing — hunting for proof the message is about something
 * *else* — was tried first and is the wrong shape: it needs a complete list of
 * ways a message can disagree, and no such list exists. Absence of evidence is
 * the signal, and it is already sitting right here.
 */

export interface SignalText {
  subject: string | null;
  /** The snippet is fine. A Gmail snippet starts at the top of the body, which
   *  is where a vendor names the couple, the date or the venue. */
  body: string;
  /** Every address on the message — From, To and Cc — already parsed. */
  addresses?: string[];
}

export type WeddingSignals = CoupleNameParts & {
  slug: string;
  /** `YYYY-MM-DD`, as stored. */
  weddingDate?: string | null;
  venueName?: string | null;
  /** The couple's own address (`weddings.contact_email`). */
  contactEmail?: string | null;
};

export type SignalKind = 'contact' | 'couple' | 'date' | 'venue' | 'slug';

export interface SignalMatch {
  /** Which of the things we know about this wedding the message mentions. */
  matched: SignalKind[];
  /** Whether the message mentions anything at all about this wedding. */
  anySignal: boolean;
  /** 0-1, for ranking Tier 4 candidates and for display in the review queue. */
  confidence: number;
  /** One sentence naming the evidence, in a planner's words. */
  rationale: string;
}

/**
 * Venue words that identify a category rather than a place. Half the wine
 * country is a "resort"; matching on that alone would make every vendor's mail
 * look like it named the venue.
 */
const GENERIC_VENUE_WORDS = new Set([
  'the', 'and', 'at', 'of', 'resort', 'hotel', 'barn', 'winery', 'vineyard',
  'vineyards', 'estate', 'estates', 'ranch', 'inn', 'club', 'house', 'gardens',
  'garden', 'farm', 'lodge', 'manor', 'spa', 'hall', 'center', 'centre',
  'venue', 'events', 'event', 'weddings', 'wedding', 'place', 'room', 'villa',
]);

const MONTHS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];

/** `September`, `Sept`, `Sep`, `Sept.` — all the ways a month gets written. */
const MONTH_PATTERN = [...MONTHS.flatMap((m) => [m, m.slice(0, 3)]), 'sept']
  .filter((m, i, all) => all.indexOf(m) === i)
  .sort((a, b) => b.length - a.length)
  .join('|');

/**
 * What each signal is worth.
 *
 * `contact` is in a different class from the rest: it is the couple's own
 * address matched exactly, not a word found in prose, so it cannot collide the
 * way a common first name or a popular Saturday can. The misfiled email was
 * addressed to the other couple the entire time — their address sat in the To
 * line while every text signal found nothing.
 *
 * Below that, couple names and the date are near-conclusive on their own; a
 * venue is a real but weaker hint, since one venue hosts many weddings. Tuned
 * so any single signal clears the review threshold and two clear it
 * comfortably.
 */
const WEIGHTS: Record<SignalKind, number> = {
  contact: 0.95,
  couple: 0.55,
  date: 0.5,
  venue: 0.3,
  slug: 0.55,
};

export function matchWeddingSignals(text: SignalText, wedding: WeddingSignals): SignalMatch {
  const haystack = foldForMatching(`${text.subject ?? ''}\n${text.body}`);
  const matched: SignalKind[] = [];
  const hits: string[] = [];

  // First because it is exact. Everything below is a word found in prose.
  const contact = wedding.contactEmail?.trim().toLowerCase();
  if (contact && (text.addresses ?? []).some((a) => a.trim().toLowerCase() === contact)) {
    matched.push('contact');
    hits.push(`the couple's own address (${contact})`);
  }

  for (const token of coupleMatchTokens(wedding)) {
    if (containsWord(haystack, token)) {
      if (!matched.includes('couple')) matched.push('couple');
      hits.push(`the name "${token}"`);
      break;
    }
  }

  if (wedding.weddingDate && datesIn(`${text.subject ?? ''}\n${text.body}`).includes(wedding.weddingDate)) {
    matched.push('date');
    hits.push(`the wedding date ${wedding.weddingDate}`);
  }

  const venueToken = distinctiveVenueTokens(wedding.venueName).find((t) => containsWord(haystack, t));
  if (venueToken) {
    matched.push('venue');
    hits.push(`the venue "${venueToken}"`);
  }

  if (containsWord(haystack, foldForMatching(wedding.slug))) {
    matched.push('slug');
    hits.push('the wedding’s label');
  }

  const confidence = Math.min(
    0.97,
    matched.reduce((sum, kind) => sum + WEIGHTS[kind], 0),
  );

  return {
    matched,
    anySignal: matched.length > 0,
    confidence,
    rationale:
      hits.length > 0
        ? `Mentions ${hits.join(', ')}`
        : 'Says nothing about this couple, their date or their venue',
  };
}

/**
 * Every date the text names, as `YYYY-MM-DD`.
 *
 * Used to ask whether *this wedding's* date appears, which is a much safer
 * question than asking what some date in the text means. A vendor's body is
 * thick with dates that are not the wedding's — out-of-office ranges, invoice
 * terms, meeting times — and all of them are harmless here: a false positive
 * needs the text to contain this exact date by coincidence.
 */
function datesIn(raw: string): string[] {
  const out: string[] = [];

  // 6/12/27, 6-12-2027. Month-first — this product's planners are US-based.
  for (const m of raw.matchAll(/\b(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})\b/g)) {
    push(out, Number(m[3]), Number(m[1]), Number(m[2]));
  }

  // June 12, 2027 — and 12 June 2027, which reverses without ambiguity because
  // a month name cannot be mistaken for a day.
  const named = new RegExp(
    `\\b(?:(${MONTH_PATTERN})\\.?\\s+(\\d{1,2})(?:st|nd|rd|th)?,?\\s+(\\d{4})` +
      `|(\\d{1,2})(?:st|nd|rd|th)?\\s+(${MONTH_PATTERN})\\.?,?\\s+(\\d{4}))\\b`,
    'gi',
  );
  for (const m of raw.matchAll(named)) {
    const month = monthNumber((m[1] ?? m[5])!);
    if (month) push(out, Number(m[3] ?? m[6]), month, Number(m[2] ?? m[4]));
  }

  for (const m of raw.matchAll(/\b(\d{4})-(\d{2})-(\d{2})\b/g)) {
    push(out, Number(m[1]), Number(m[2]), Number(m[3]));
  }

  return out;
}

function push(out: string[], year: number, month: number, day: number): void {
  if (month < 1 || month > 12 || day < 1 || day > 31) return;
  // A two-digit year in wedding mail is always this century — these are future
  // events, and `27` meaning 1927 is not a reading anyone intends.
  const full = year < 100 ? 2000 + year : year;
  if (full < 1900 || full > 2200) return;
  out.push(`${full}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
}

function monthNumber(word: string): number | null {
  const folded = word.toLowerCase().replace(/\.$/, '');
  const index = MONTHS.findIndex((m) => m.startsWith(folded));
  return index === -1 ? null : index + 1;
}

function distinctiveVenueTokens(venueName: string | null | undefined): string[] {
  return foldForMatching(venueName ?? '')
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length >= 4 && !GENERIC_VENUE_WORDS.has(t));
}
